import capitalize from 'lodash/capitalize';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { BsLink } from '@hotelplan/core.basis.bs-link';
import { useTransfer } from '@hotelplan/fdr.lib.router.push';
import { FdrRoute } from '@hotelplan/fdr.regular.basis.fdr-route';
import { FdrTransferInfo } from '@hotelplan/fdr.regular.basis.fdr-transfer-info';
import { mapFeatureNameToIcon } from '@hotelplan/libs.mappers';
import { formatDate, parseDateStr } from '@hotelplan/libs.utils';
import { useWishlistSelectedItemsContext } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist-selected-items-context';
import { FdrOfferBreadcrumbs } from 'fdr/components/local/fdr-offer';
import { mapFdrOrlFlightToIFdrFlightSectionProps } from 'fdr/components/local/fdr-orl/fdr-orl.mappers';
import {
  FdrProductCardImage,
  FdrProductCardRating,
  FdrProductCardSubTitle,
  FdrProductCardTitle,
} from 'fdr/components/local/fdr-product-card/fdr-product-card.styles';
import { FdrWishlistItemFragment } from 'fdr/schemas/fragment/wishlist/fdr-wishlist.generated';
import { RatingWrapper } from './fdr-wishlist-compare-items-desktop.styles';
import { FdrWishlistCompareItemsMobilePriceButton } from './fdr-wishlist-compare-items-mobile-price-button';
import {
  ColumnWrapper,
  FlightSectionShortWrapper,
  IncludedInPriceHeader,
  ItemsToShowNoBorderWrapper,
  ItemsToShowPriceButtonWrapper,
  ItemsToShowWrapper,
  RoomsWrapper,
  SectionHeader,
  SectionWrapper,
  TextBlockWrapper,
  TopOfferWrapper,
  TransferInfoWrapper,
  TravelTypeIconWrapper,
  TravelTypeWrapper,
  TripAdvisorInfoWrapper,
  WishlistCompareItemFeaturesMobileWrapper,
  WishlistCompareItemsMobileWrapper,
  WishlistCompareNotSelectedItemInfoWrapper,
  WishlistImageWrapper,
  WishlistItemOverlayWrapper,
  WishlistOrlLinkWrapper,
  WishlistProductTravelType,
  WishlistImageLink,
} from './fdr-wishlist-compare-items-mobile.styles';
import { COMPARE_ITEMS_COUNT_MOBILE } from './fdr-wishlist.constants';
import {
  getOfferLinkFromWishlistItem,
  mapWishlistSelectedItemToCompareItem,
} from './fdr-wishlist.utils';

const WishlistOrlLink: React.FC<{ item: FdrWishlistItemFragment }> = ({
  item,
}) => {
  const { transfer } = useTransfer();
  const [t] = useTranslation(['common']);
  const offerLink = getOfferLinkFromWishlistItem(item);

  return (
    <WishlistOrlLinkWrapper>
      <BsLink
        href="#"
        onClick={e => {
          e.preventDefault();
          transfer(offerLink);
        }}
      >
        {t('common:checkOffer')} <BsIcon name="chevron-long-right" />
      </BsLink>
    </WishlistOrlLinkWrapper>
  );
};

const EmptyColumn: React.FC<{ length: number; index: number }> = ({
  index,
  length,
}) => {
  return <SectionWrapper i={index} length={length} />;
};

const TOP_HOTEL_OFFER_ID = 'hotel_top';

const FdrWishlistCompareItemsMobile: React.FC<{
  onCheckout: (item: FdrWishlistItemFragment) => void;
}> = props => {
  const { onCheckout } = props;
  const [t, i18n] = useTranslation(['common', 'forms']);

  const { items, updateItem, selectedItems } =
    useWishlistSelectedItemsContext();

  const selectedItemsToShow = selectedItems
    .slice(0, COMPARE_ITEMS_COUNT_MOBILE)
    .map(mapWishlistSelectedItemToCompareItem);

  const canShowNotSelectedInfo =
    !items || items.length < COMPARE_ITEMS_COUNT_MOBILE;
  const showTransferInfo = selectedItems.some(
    item =>
      item?.trackedOffer.latest?.transferPrice ||
      item?.trackedOffer.latest?.transferIncluded
  );

  return (
    <WishlistCompareItemsMobileWrapper>
      <ItemsToShowWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return canShowNotSelectedInfo ? (
              <WishlistCompareNotSelectedItemInfoWrapper
                i={i}
                length={selectedItemsToShow.length}
              />
            ) : (
              <ColumnWrapper i={i} length={selectedItemsToShow.length} />
            );
          }

          const topOfferFeatures = item.summaryFeatureGroups?.filter(
            feature => feature.id === TOP_HOTEL_OFFER_ID
          );

          return (
            <ColumnWrapper
              key={`main-info-${i}`}
              i={i}
              length={selectedItemsToShow.length}
            >
              <TravelTypeWrapper>
                <WishlistProductTravelType>
                  {item.productTypeData.map((type, y) => {
                    return (
                      <React.Fragment key={`type-${y}`}>
                        <TravelTypeIconWrapper>
                          <BsIcon
                            name={type.icon}
                            className="travel-type-icon"
                          />
                        </TravelTypeIconWrapper>
                        {item.productTypeData.length - 1 !== y && (
                          <div className="delimiter"> + </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </WishlistProductTravelType>
                {topOfferFeatures?.length ? (
                  <TopOfferWrapper>
                    {topOfferFeatures.map((feature, index) => {
                      return (
                        <React.Fragment key={index}>
                          <span>
                            <BsIcon
                              name={mapFeatureNameToIcon(feature.id)}
                              size="md"
                            />
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </TopOfferWrapper>
                ) : null}
              </TravelTypeWrapper>
              {item.image && (
                <WishlistImageLink link={item.productLink}>
                  <WishlistImageWrapper>
                    <FdrProductCardImage image={item.image} />
                  </WishlistImageWrapper>
                </WishlistImageLink>
              )}
              {item.rating > 0 && (
                <RatingWrapper>
                  <FdrProductCardRating
                    maxRating={5}
                    rating={item.rating}
                    ratingContent={<BsIcon name="hotelplanbewertung" />}
                    halfWayRatingContent={
                      <BsIcon name="hotelplanbewertung-outlined" />
                    }
                  />
                </RatingWrapper>
              )}
              <FdrProductCardTitle>
                <FdrRoute link={item.link}>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid*/}
                  <a>{item.name}</a>
                </FdrRoute>
              </FdrProductCardTitle>
              <FdrProductCardSubTitle>
                <FdrOfferBreadcrumbs
                  className="bread-crumbs"
                  breadcrumbs={item.breadcrumbs}
                />
              </FdrProductCardSubTitle>
            </ColumnWrapper>
          );
        })}
      </ItemsToShowWrapper>
      <ItemsToShowWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return (
              <EmptyColumn index={i} length={selectedItemsToShow.length} />
            );
          }

          return (
            <SectionWrapper
              i={i}
              length={selectedItemsToShow.length}
              key={`flights-${i}`}
            >
              <SectionHeader>
                <BsIcon name={'flug'} className="section-lg-icon" />
                {t('forms:returnFlight.radioInput')}
              </SectionHeader>
              {item.forwardFlight && (
                <FlightSectionShortWrapper
                  {...mapFdrOrlFlightToIFdrFlightSectionProps(
                    item.forwardFlight
                  )}
                  displayAirportName={false}
                  displayArrivalAirport
                  className="flight-section-short"
                />
              )}
              {item.returnFlight && (
                <FlightSectionShortWrapper
                  {...mapFdrOrlFlightToIFdrFlightSectionProps(
                    item.returnFlight
                  )}
                  displayAirportName={false}
                  displayArrivalAirport
                  className="flight-section-short no-mb"
                />
              )}
              {!item.forwardFlight && !item.returnFlight && (
                <p>{t('forms:noFlight.title')}</p>
              )}
            </SectionWrapper>
          );
        })}
      </ItemsToShowWrapper>
      <ItemsToShowNoBorderWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return (
              <EmptyColumn index={i} length={selectedItemsToShow.length} />
            );
          }

          return (
            <SectionWrapper
              key={`main-info-${i}`}
              i={i}
              length={selectedItemsToShow.length}
            >
              <SectionHeader>
                <BsIcon name={'hotel'} className="section-md-icon" />
                {t('common:HOTEL')},{' '}
                {t('nights.count', {
                  count: item.duration,
                })}
              </SectionHeader>
              <div>
                <p>
                  {`${capitalize(
                    formatDate(
                      parseDateStr(item.departureDate),
                      'dd.MM.yyyy',
                      i18n.language
                    )
                  )} - ${capitalize(
                    formatDate(
                      parseDateStr(item.returnDate),
                      'dd.MM.yyyy',
                      i18n.language
                    )
                  )}`}
                </p>
              </div>
            </SectionWrapper>
          );
        })}
      </ItemsToShowNoBorderWrapper>
      <ItemsToShowWrapper>
        {selectedItemsToShow.map((item, i) => {
          if (!item) {
            return (
              <EmptyColumn index={i} length={selectedItemsToShow.length} />
            );
          }

          return (
            <SectionWrapper
              key={`rooms-${i}`}
              i={i}
              length={selectedItemsToShow.length}
            >
              {item.rooms.map((room, y) => {
                return (
                  <RoomsWrapper key={`room-description-${y}`}>
                    <span className="wishlist-room">
                      {t('common:room')} {y + 1}
                    </span>
                    <p>
                      {room.roomDescription}, {room.mealDescription}
                    </p>
                    <p className="children-rooms">
                      {t('common:adult.count', { count: room.adults })}
                      {(room?.childrenDobs || []).length > 0 &&
                        `, ${t('common:child.count', {
                          count: room.childrenDobs.length,
                        })}`}
                    </p>
                  </RoomsWrapper>
                );
              })}
            </SectionWrapper>
          );
        })}
      </ItemsToShowWrapper>
      <ItemsToShowWrapper>
        {selectedItemsToShow.map((item, i) => {
          const currentWishlistItem = selectedItems.find(
            selectedItem => selectedItem?.product?.id === item?.productId
          );

          return (
            <SectionWrapper
              key={`prices-${i}`}
              i={i}
              length={selectedItemsToShow.length}
            >
              <FdrWishlistCompareItemsMobilePriceButton
                item={currentWishlistItem}
                onCheckout={onCheckout}
              />
              <WishlistOrlLink item={currentWishlistItem} />
            </SectionWrapper>
          );
        })}
      </ItemsToShowWrapper>
      {showTransferInfo && (
        <ItemsToShowWrapper>
          {selectedItemsToShow.map((item, i) => {
            if (!item) {
              return (
                <EmptyColumn index={i} length={selectedItemsToShow.length} />
              );
            }

            return (
              <TransferInfoWrapper
                key={`transferInfo-${i}`}
                i={i}
                length={selectedItemsToShow.length}
              >
                {item.transferPrice || item.transferIncluded ? (
                  <FdrTransferInfo
                    price={item.transferPrice}
                    transferIncluded={item.transferIncluded}
                  />
                ) : (
                  t('wishlist:transfer.notAvailable.text')
                )}
              </TransferInfoWrapper>
            );
          })}
        </ItemsToShowWrapper>
      )}
      <WishlistCompareItemFeaturesMobileWrapper items={selectedItemsToShow} />
      {selectedItemsToShow.some(i => i?.includedInPrice) && (
        <ItemsToShowWrapper>
          {selectedItemsToShow.map((item, i) => {
            if (!item || !item?.includedInPrice) {
              return (
                <EmptyColumn index={i} length={selectedItemsToShow.length} />
              );
            }

            return (
              <SectionWrapper
                key={`includedInPrice-${i}`}
                i={i}
                length={selectedItemsToShow.length}
              >
                {item.includedInPrice.title && (
                  <IncludedInPriceHeader>
                    <BsIcon name="money-wallet" />
                    {t('wishlist:includeInPrice.mobile.title')}
                  </IncludedInPriceHeader>
                )}
                <TextBlockWrapper
                  data-id="text-block-wrapper"
                  title={''}
                  text={item.includedInPrice.text}
                />
              </SectionWrapper>
            );
          })}
        </ItemsToShowWrapper>
      )}
      {selectedItemsToShow.some(i => i?.taRating > 0 && i?.taReviews > 0) && (
        <ItemsToShowWrapper>
          {selectedItemsToShow.map((item, i) => {
            if (!item || item.taRating <= 0 || item.taReviews <= 0) {
              return (
                <EmptyColumn index={i} length={selectedItemsToShow.length} />
              );
            }

            return (
              <div key={`tripAdvisor-${i}`}>
                <TripAdvisorInfoWrapper
                  rating={item.taRating}
                  count={item.taReviews || 0}
                  label={t('common:tripAdvisorShort.label')}
                />
              </div>
            );
          })}
        </ItemsToShowWrapper>
      )}
      <ItemsToShowPriceButtonWrapper>
        {selectedItemsToShow.map((item, i) => {
          const currentWishlistItem = selectedItems.find(
            selectedItem => selectedItem?.product?.id === item?.productId
          );

          return (
            <SectionWrapper
              key={`prices-${i}`}
              i={i}
              length={selectedItemsToShow.length}
            >
              <FdrWishlistCompareItemsMobilePriceButton
                item={currentWishlistItem}
                onCheckout={onCheckout}
              />
              <WishlistOrlLink item={currentWishlistItem} />
            </SectionWrapper>
          );
        })}
      </ItemsToShowPriceButtonWrapper>
      {selectedItemsToShow.map((item, i) => {
        if (!item) return null;

        const currentWishlistItem = selectedItems.find(
          selectedItem => selectedItem.product.id === item.productId
        );

        return (
          <WishlistItemOverlayWrapper
            key={`overlay-${i}`}
            item={currentWishlistItem}
            index={i}
            onAccept={acceptedOffer => {
              updateItem(acceptedOffer);
            }}
          />
        );
      })}
    </WishlistCompareItemsMobileWrapper>
  );
};

export default FdrWishlistCompareItemsMobile;
